import axios from 'axios';
import { getCookie } from '../utils/cookies';
import { GoAuthApi } from './goApi';

// FAIL FAST: URL for Rust API via runtime configuration
const getRustApiUrl = () => {
  // FAIL FAST: Configuration must exist and be valid
  if (!window.CONFIG) {
    throw new Error('FATAL: window.CONFIG is not defined. Cannot determine Rust API URL.');
  }

  if (window.CONFIG.baseUrl === undefined || window.CONFIG.baseUrl === null) {
    throw new Error('FATAL: window.CONFIG.baseUrl is not defined. Cannot construct API URL.');
  }

  if (!window.CONFIG.apiEndpoints) {
    throw new Error('FATAL: window.CONFIG.apiEndpoints is not defined. Cannot determine API endpoints.');
  }

  if (!window.CONFIG.apiEndpoints.rust) {
    throw new Error('FATAL: window.CONFIG.apiEndpoints.rust is not defined. Cannot determine Rust API endpoint.');
  }

  // Configuration is valid, construct URL
  // Handle empty string baseUrl for development environment (creates relative URL)
  const baseUrl = window.CONFIG.baseUrl || '';
  const apiUrl = baseUrl + window.CONFIG.apiEndpoints.rust;
  console.log('[RustAPI] Using API URL from configuration:', apiUrl);

  return apiUrl;
};

// Remove static calculation, will calculate dynamically
// const RUST_API_URL = getRustApiUrl();

// Class for working with Rust API
export class RustCalculatorApi {
  // Updated method to get token with priority on Clerk Session Token
  static async getAuthToken() {
    console.log('[RustAPI] Getting authentication token...');

    // 1. Try to get from active Clerk.js session (most reliable way)
    if (window.Clerk && window.Clerk.session) {
      try {
        console.log('[RustAPI] Attempting to get token from Clerk.session.getToken()...');
        const clerkToken = await window.Clerk.session.getToken();
        if (clerkToken) {
          console.log('[RustAPI] Got token from Clerk session, length:', clerkToken.length);
          // Less strict validation - check only basic JWT format
          if (clerkToken.split('.').length === 3) {
            console.log('[RustAPI] Token format is valid JWT');
            // Cache token for other components
            window.__GLOBAL_CLERK_TOKEN_CACHE = clerkToken;
            localStorage.setItem('__session', clerkToken);
            return clerkToken;
          }
        }
      } catch (e) {
        console.warn('[RustAPI] Error getting token from Clerk.session:', e);
      }
    } else {
      console.log('[RustAPI] Clerk or Clerk.session not available');
    }

    // 2. Try from global cache that GoAuthApi might have set
    if (window.__GLOBAL_CLERK_TOKEN_CACHE) {
      const cachedToken = window.__GLOBAL_CLERK_TOKEN_CACHE;
      // Basic JWT format check
      if (cachedToken.split('.').length === 3) {
        console.log('[RustAPI] Using token from __GLOBAL_CLERK_TOKEN_CACHE');
        return cachedToken;
      }
    }
    
    // 3. Try from GoAuthApi cache
    if (GoAuthApi.cachedClerkToken) {
      const cachedToken = GoAuthApi.cachedClerkToken;
      // Basic JWT format check
      if (cachedToken.split('.').length === 3) {
        console.log('[RustAPI] Using token from GoAuthApi.cachedClerkToken');
        return cachedToken;
      }
    }

    // 4. If this is Electron, try to get from Electron store
    if (window.electronAPI && window.electronAPI.getClerkSessionToken) {
      try {
        const electronToken = await window.electronAPI.getClerkSessionToken();
        if (electronToken && electronToken.split('.').length === 3) {
          console.log('[RustAPI] Got token from electronAPI.getClerkSessionToken');
          return electronToken;
        }
      } catch (e) {
        console.warn('[RustAPI] Error getting token from electronAPI.getClerkSessionToken:', e);
      }
    }
    
    // 5. Try to get token through GoAuthApi (which has complex logic)
    try {
      const goApiToken = await GoAuthApi.getAuthToken();
      if (goApiToken && goApiToken.split('.').length === 3) {
        console.log('[RustAPI] Got token from GoAuthApi.getAuthToken');
        return goApiToken;
      }
    } catch (e) {
      console.warn('[RustAPI] Error getting token from GoAuthApi.getAuthToken:', e);
    }
    
    // 6. Fallback: localStorage (less preferred for Clerk session tokens)
    const lsToken = localStorage.getItem('__session'); // Clerk usually stores here
    if (lsToken && lsToken.split('.').length === 3) {
      console.log('[RustAPI] Using token from localStorage.__session');
      return lsToken;
    }

    // 7. Last fallback: legacy methods (for backward compatibility)
    if (window.electronAPI && window.electronAPI.getAuthToken) {
      try {
        const legacyToken = await window.electronAPI.getAuthToken();
        if (legacyToken && legacyToken.split('.').length === 3) {
          console.log('[RustAPI] Using legacy token from electronAPI.getAuthToken');
          return legacyToken;
        }
      } catch (e) {
        console.warn('[RustAPI] Error getting legacy token:', e);
      }
    }
    
    // 8. Final fallback: legacy cookie/localStorage
    const cookieToken = getCookie('auth_token');
    if (cookieToken && cookieToken.split('.').length === 3) {
      console.log('[RustAPI] Using legacy token from auth_token cookie');
      return cookieToken;
    }
    
    const legacyLsToken = localStorage.getItem('auth_token');
    if (legacyLsToken && legacyLsToken.split('.').length === 3) {
      console.log('[RustAPI] Using legacy token from auth_token localStorage');
      return legacyLsToken;
    }

    console.error('[RustAPI] Failed to get a valid auth token from any source.');
    return null; // If token not found anywhere
  }

  // Main method for performing calculations
  static async calculate(calculatorData) {
    console.log('[RustAPI] Starting calculation request...');
    
    let token = null;
    let tokenSource = 'unknown';
    
    // FIX: Improved token retrieval logic with multiple attempts
    const getValidToken = async () => {
      // Attempt 1: Fresh token from Clerk directly (priority method)
      if (window.Clerk && window.Clerk.session) {
        try {
          const clerkToken = await window.Clerk.session.getToken();
          if (clerkToken) {
            console.log('[RustAPI] Got token from Clerk.session.getToken()');
            return { token: clerkToken, source: 'Clerk.session' };
          }
        } catch (e) {
          console.warn('[RustAPI] Error getting token from Clerk.session:', e);
        }
      }
      
      // Attempt 2: Global token cache
      if (window.__GLOBAL_CLERK_TOKEN_CACHE) {
        const cachedToken = window.__GLOBAL_CLERK_TOKEN_CACHE;
        // Check token with less strict validation (only basic format check)
        if (cachedToken && cachedToken.split('.').length === 3) {
          console.log('[RustAPI] Using token from global cache');
          return { token: cachedToken, source: 'GlobalCache' };
        }
      }
      
      // Attempt 3: localStorage Clerk token
      const lsToken = localStorage.getItem('__session');
      if (lsToken && lsToken.split('.').length === 3) {
        console.log('[RustAPI] Using token from localStorage.__session');
        return { token: lsToken, source: 'localStorage' };
      }
      
      // Attempt 4: Complex getAuthToken method
      try {
        const authToken = await this.getAuthToken();
        if (authToken) {
          console.log('[RustAPI] Got token from getAuthToken() method');
          return { token: authToken, source: 'getAuthToken' };
        }
      } catch (e) {
        console.warn('[RustAPI] Error from getAuthToken():', e);
      }
      
      return { token: null, source: 'none' };
    };
    
    // Get token
    const tokenResult = await getValidToken();
    token = tokenResult.token;
    tokenSource = tokenResult.source;
    
    if (!token) {
      console.error('[RustAPI] No token available from any source');
      throw new Error('Authentication token is required for calculations. Please sign in.');
    }
    
    console.log(`[RustAPI] Using token from: ${tokenSource}, length: ${token.length}`);
    console.log(`[RustAPI] Token prefix: ${token.substring(0, 20)}...`);
    
    // FIX: Remove strict token validation before first attempt
    // Let server validate token, which gives us more error information
    let needsRefresh = false;

    // Only basic JWT format check (should be 3 parts separated by dots)
    if (!token.includes('.') || token.split('.').length !== 3) {
      console.warn('[RustAPI] Token does not appear to be valid JWT format');
      needsRefresh = true;
    } else {
      // Optional expiration check, but not blocking
      try {
        const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.exp && payload.exp <= Math.floor(Date.now() / 1000)) {
          console.log('[RustAPI] Token appears to be expired, will attempt refresh');
          needsRefresh = true;
        }
      } catch (e) {
        console.warn('[RustAPI] Could not parse token payload, proceeding with request');
      }
    }
    
    // If token appears expired, try to refresh it preemptively
    if (needsRefresh) {
      console.log('[RustAPI] Attempting preemptive token refresh...');
      try {
        if (window.Clerk && window.Clerk.session) {
          const freshToken = await window.Clerk.session.getToken();
          if (freshToken && freshToken !== token) {
            token = freshToken;
            tokenSource = 'Clerk.session.refresh';
            console.log('[RustAPI] Successfully refreshed token preemptively');
          }
        }
      } catch (refreshError) {
        console.warn('[RustAPI] Preemptive token refresh failed, proceeding with original token:', refreshError);
      }
    }
    
    try {
      // Prepare request headers
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      
      // FIX: Make sure Authorization header is always set correctly
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[RustAPI] Authorization header set with Bearer token');
      } else {
        console.error('[RustAPI] No token available for Authorization header');
      }
      
      // Add detailed request logging for debugging
      const apiUrl = getRustApiUrl();
      console.log(`[RustAPI] Making calculation request to: ${apiUrl}`);
      console.log(`[RustAPI] Token source: ${tokenSource}`);
      console.log(`[RustAPI] Token length: ${token.length}`);
      console.log(`[RustAPI] Token prefix: ${token.substring(0, 20)}...`);
      console.log('[RustAPI] Request headers:', Object.keys(headers));
      console.log('[RustAPI] Authorization header present:', !!headers['Authorization']);
      console.log('[RustAPI] Request data keys:', Object.keys(calculatorData));
      
      // Execute request with timeout
      const response = await axios.post(apiUrl, calculatorData, {
        headers,
        withCredentials: true, // Important for reading and sending cookie
        timeout: 30000, // 30 seconds timeout for calculations
        validateStatus: function (status) {
          // Accept statuses 200-299 and some errors for handling
          return (status >= 200 && status < 300) || [401, 402, 404, 500].includes(status);
        }
      });
      
      console.log(`[RustAPI] Request completed with status: ${response.status}`);
      console.log('[RustAPI] Response headers:', Object.keys(response.headers));
      
      // Check response success
      if (response.status >= 200 && response.status < 300) {
        console.log('[RustAPI] Calculation successful');
        console.log('[RustAPI] Response data keys:', Object.keys(response.data || {}));
        return response.data;
      }

      // Retry once on 500 (server closed idle connection during deployment)
      if (response.status === 500) {
        console.log('[RustAPI] Got 500, retrying once...');
        const retryResponse = await axios.post(apiUrl, calculatorData, {
          headers,
          withCredentials: true,
          timeout: 30000,
          validateStatus: function (status) {
            return status >= 200 && status < 300;
          }
        });
        console.log('[RustAPI] Retry successful');
        return retryResponse.data;
      }

      // If status is not successful but we accepted it for handling, throw an error
      throw new Error(`Request failed with status ${response.status}: ${response.data?.error || 'Unknown error'}`);
      
    } catch (error) {
      console.error('[RustAPI] Error during calculation:', error);
      console.error(`[RustAPI] Error type: ${error.constructor.name}`);
      console.error(`[RustAPI] Error message: ${error.message}`);
      
      // Check for specific errors
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;
        
        console.error(`[RustAPI] HTTP ${status} response:`, responseData);
        console.error(`[RustAPI] Response headers:`, error.response.headers);
        
        if (status === 401) {
          // Try to refresh token and retry request once
          console.log('[RustAPI] 401 Unauthorized - attempting token refresh and retry...');
          console.log(`[RustAPI] Current token source: ${tokenSource}`);
          console.log(`[RustAPI] Current token prefix: ${token.substring(0, 20)}...`);
          
          try {
            // Clear token cache before refreshing
            window.__GLOBAL_CLERK_TOKEN_CACHE = null;
            localStorage.removeItem('__session');
            
            // Get new token
            let freshToken = null;
            if (window.Clerk && window.Clerk.session) {
              freshToken = await window.Clerk.session.getToken();
              console.log('[RustAPI] Got fresh token from Clerk.session.getToken()');
            } else {
              freshToken = await GoAuthApi.refreshToken();
              console.log('[RustAPI] Got fresh token from GoAuthApi.refreshToken()');
            }
              
            if (freshToken && freshToken !== token) {
              console.log('[RustAPI] Token successfully refreshed, retrying calculation...');
              console.log(`[RustAPI] New token length: ${freshToken.length}`);
              console.log(`[RustAPI] New token prefix: ${freshToken.substring(0, 20)}...`);
              
              // Save new token to cache
              window.__GLOBAL_CLERK_TOKEN_CACHE = freshToken;
              localStorage.setItem('__session', freshToken);
              
              const retryHeaders = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Authorization': `Bearer ${freshToken}`
              };
              
              console.log('[RustAPI] Making retry request with fresh token...');
              const retryResponse = await axios.post(getRustApiUrl(), calculatorData, {
                headers: retryHeaders,
                withCredentials: true,
                timeout: 30000,
                validateStatus: function (status) {
                  return status >= 200 && status < 300;
                }
              });
              
              console.log('[RustAPI] Calculation successful after token refresh');
              return retryResponse.data;
            } else {
              console.error('[RustAPI] Token refresh returned same or empty token');
            }
          } catch (retryError) {
            console.error('[RustAPI] Token refresh and retry failed:', retryError);
            if (retryError.response) {
              console.error('[RustAPI] Retry response status:', retryError.response.status);
              console.error('[RustAPI] Retry response data:', retryError.response.data);
            }
          }
          
          throw new Error('Authentication failed. Please sign in again.');
        } else if (status === 402) {
          throw new Error('Active subscription required.');
        } else if (status === 404) {
          throw new Error('Calculation service is not available. Please try again later.');
        } else if (status === 500) {
          throw new Error('Server error occurred. Please try again later.');
        } else {
          const errorMessage = responseData?.error || responseData?.message || 'Unknown error';
          throw new Error(`Calculation error (${status}): ${errorMessage}`);
        }
      } else if (error.request) {
        console.error('[RustAPI] No response received from server');
        console.error('[RustAPI] Request config:', error.config);
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        console.error('[RustAPI] Error setting up request:', error.message);
        throw new Error(`Request setup error: ${error.message}`);
      }
    }
  }

  // Method for futures calculations
  static async calculateFutures(futuresData) {
    console.log('[RustAPI] Starting futures calculation request...');

    let token = null;
    let tokenSource = 'unknown';

    // Get token using existing logic
    const getValidToken = async () => {
      if (window.Clerk && window.Clerk.session) {
        try {
          const clerkToken = await window.Clerk.session.getToken();
          if (clerkToken) {
            console.log('[RustAPI] Got token from Clerk.session.getToken()');
            return { token: clerkToken, source: 'Clerk.session' };
          }
        } catch (e) {
          console.warn('[RustAPI] Error getting token from Clerk.session:', e);
        }
      }

      if (window.__GLOBAL_CLERK_TOKEN_CACHE) {
        const cachedToken = window.__GLOBAL_CLERK_TOKEN_CACHE;
        if (cachedToken && cachedToken.split('.').length === 3) {
          console.log('[RustAPI] Using token from global cache');
          return { token: cachedToken, source: 'GlobalCache' };
        }
      }

      const lsToken = localStorage.getItem('__session');
      if (lsToken && lsToken.split('.').length === 3) {
        console.log('[RustAPI] Using token from localStorage.__session');
        return { token: lsToken, source: 'localStorage' };
      }

      try {
        const authToken = await this.getAuthToken();
        if (authToken) {
          console.log('[RustAPI] Got token from getAuthToken() method');
          return { token: authToken, source: 'getAuthToken' };
        }
      } catch (e) {
        console.warn('[RustAPI] Error from getAuthToken():', e);
      }

      return { token: null, source: 'none' };
    };

    const tokenResult = await getValidToken();
    token = tokenResult.token;
    tokenSource = tokenResult.source;

    if (!token) {
      console.error('[RustAPI] No token available from any source');
      throw new Error('Authentication token is required for calculations. Please sign in.');
    }

    console.log(`[RustAPI] Using token from: ${tokenSource}, length: ${token.length}`);

    try {
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Authorization': `Bearer ${token}`
      };

      // Construct futures API URL (same base but different endpoint)
      const baseUrl = window.CONFIG.baseUrl || '';
      const futuresApiUrl = baseUrl + '/api/futures/calculate';

      console.log(`[RustAPI] Making futures calculation request to: ${futuresApiUrl}`);
      console.log('[RustAPI] Request data:', Object.keys(futuresData));

      const response = await axios.post(futuresApiUrl, futuresData, {
        headers,
        withCredentials: true,
        timeout: 30000,
        validateStatus: function (status) {
          return (status >= 200 && status < 300) || [401, 402, 404, 500].includes(status);
        }
      });

      console.log(`[RustAPI] Futures request completed with status: ${response.status}`);

      if (response.status >= 200 && response.status < 300) {
        console.log('[RustAPI] Futures calculation successful');
        return response.data;
      }

      throw new Error(`Request failed with status ${response.status}: ${response.data?.error || 'Unknown error'}`);

    } catch (error) {
      console.error('[RustAPI] Error during futures calculation:', error);

      if (error.response) {
        const status = error.response.status;

        if (status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        } else if (status === 402) {
          throw new Error('Active subscription required.');
        } else if (status === 404) {
          throw new Error('Futures calculation service is not available.');
        } else if (status === 500) {
          throw new Error('Server error occurred. Please try again later.');
        } else {
          throw new Error(`Calculation error (${status}): ${error.response.data?.error || 'Unknown error'}`);
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your connection and try again.');
      } else {
        throw new Error(`Request setup error: ${error.message}`);
      }
    }
  }
}

export default RustCalculatorApi;
